import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AuthGate from './components/AuthGate.jsx';
import Sky from './components/Sky.jsx';
import TabBar from './components/TabBar.jsx';
import SideDrawer from './components/SideDrawer.jsx';
import ActivityWindow from './components/ActivityWindow.jsx';
import Crab from './components/Crab.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import Home from './modules/home/Home.jsx';
import Chat from './modules/chat/Chat.jsx';
import StarMap from './modules/starmap/StarMap.jsx';
import Timeline from './modules/timeline/Timeline.jsx';
import Diary from './modules/diary/Diary.jsx';
import TarotPage from './modules/tarot/TarotPage.jsx';
import Pulse from './modules/pulse/Pulse.jsx';
import Memoir from './modules/memoir/Memoir.jsx';
import Usage from './modules/usage/Usage.jsx';
import Album from './modules/album/Album.jsx';
import Pearls from './modules/pearls/Pearls.jsx';
import Cat from './modules/cat/Cat.jsx';
import Studio from './modules/studio/Studio.jsx';
import Calendar from './modules/calendar/Calendar.jsx';
import Recall from './modules/recall/Recall.jsx';
import Ying from './modules/ying/Ying.jsx';
import MusicBox from './modules/musicbox/MusicBox.jsx';
import { MusicProvider } from './modules/musicbox/MusicContext.jsx';
import { MiniBar, LyricsOverlay } from './modules/musicbox/MiniPlayer.jsx';

// 切换路由时给 ErrorBoundary 一个新 key，让它在新页面 reset state
// （key 同时让 .page-enter 每次切页重挂载 → 进场淡化动画自然重放。
//   动画只动 opacity 不动 transform：页面里有很多 position:fixed 层，
//   transform 会临时改变它们的包含块、结束瞬间跳位。）
function RoutedBoundary() {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname}>
      <div className="page-enter">
      <Routes>
        {/* 打开 app 仍直落聊天（说话零障碍是底线）；星洲是可逛不必经的 tab */}
        <Route path="/" element={<Navigate to="/chat" replace />} />
        <Route path="/home" element={<Home />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/memory" element={<StarMap />} />
        <Route path="/timeline" element={<Timeline />} />
        <Route path="/diary" element={<Diary />} />
        <Route path="/tarot" element={<TarotPage />} />
        <Route path="/pulse" element={<Pulse />} />
        <Route path="/memoir" element={<Memoir />} />
        <Route path="/usage" element={<Usage />} />
        <Route path="/album" element={<Album />} />
        <Route path="/pearls" element={<Pearls />} />
        <Route path="/cat" element={<Cat />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/recall" element={<Recall />} />
        <Route path="/ying" element={<Ying />} />
        <Route path="/musicbox" element={<MusicBox />} />
        <Route path="*" element={<Navigate to="/chat" replace />} />
      </Routes>
      </div>
    </ErrorBoundary>
  );
}

export default function App() {
  const location = useLocation();
  const isChat = location.pathname === '/chat' || location.pathname === '/';

  // 聊天页标记：常驻小播放条在聊天页藏起来（音乐照放），底部只留输入框+导航，不挤。
  useEffect(() => {
    document.body.classList.toggle('route-chat', isChat);
  }, [isChat]);

  // iOS standalone：外壳用 position:fixed 双边锚定视口（top:0 + bottom:0），
  // 稳定贴到屏幕底（比 100dvh 可靠，不会在导航栏下方留缝）。这里只负责算出
  // 键盘占住的高度写进 --kb：键盘弹起 → 外壳下沿抬到键盘上方；收起 → 归 0。
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    // 读一次「当前键盘高度」并夹住上限。上限夹住（2026-08-15 铃录屏逮到「弹键盘那一瞬间
    // 闪一下、露出一大块纯底色」）：iOS 在键盘弹出动画中途某一帧会给出 vv.height / vv.offsetTop
    // 非原子更新的瞬时坏值，让键盘高远超真实值（那帧实测冲到约屏高 75%，真键盘含辅助条才约 41%）
    // → fixed 外壳(.app-shell bottom:var(--kb)) 下沿被抬过头、瞬间塌短 → 露底色。真键盘顶天半屏
    // 多点，超 60% 一律当坏帧封顶。
    const readKb = () =>
      Math.max(
        0,
        Math.min(window.innerHeight - vv.height - vv.offsetTop, window.innerHeight * 0.6),
      );
    let lastKb = 0;
    let confirmRaf = 0;
    const commit = (kb) => {
      const open = kb > 80;
      root.style.setProperty('--kb', (open ? kb : 0) + 'px');
      // 键盘弹起时隐藏底部导航，让输入框直接贴键盘上方（标准聊天行为）
      root.classList.toggle('kb-open', open);
      lastKb = kb;
    };
    const update = () => {
      const kb = readKb();
      // 再滤一层「单帧向上尖峰」（60% 硬顶漏网的、没那么离谱但仍会闪的坏帧，铃实测 5 次跳 2 次）：
      // iOS 键盘动画途中偶尔蹦一帧离谱变高→外壳塌一下露底。凡是比上一次明显变高(>40px)的，
      // 先推迟一帧、下一帧重读再定：真开键盘→下一帧还高、照常收(只慢 ~16ms 无感)；坏帧尖峰→
      // 下一帧已落回、这一帧的塌就被跳过，不闪。变低/小幅变化即时生效（关键盘不拖延）。
      cancelAnimationFrame(confirmRaf);
      if (kb > lastKb + 40) {
        confirmRaf = requestAnimationFrame(() => commit(readKb()));
      } else {
        commit(kb);
      }
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      cancelAnimationFrame(confirmRaf);
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // ③ 在线心跳：在看页面时每 12s 告诉后端「我在」；切到后台/锁屏时告诉「我走了」。
  // 后端据此决定 Sael 回复收完要不要推手机——你在看就不推、走开才推。
  useEffect(() => {
    const ping = (here) => {
      try {
        fetch('/api/presence', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ here }),
          keepalive: !here, // 切后台那一下用 keepalive，保证「我走了」发得出去
        }).catch(() => {});
      } catch {}
    };
    const beat = () => {
      if (!document.hidden) ping(true);
    };
    beat();
    const timer = setInterval(beat, 12000);
    const onVis = () => ping(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <AuthGate>
      <MusicProvider>
        <Sky />
        <SideDrawer />
        <ActivityWindow />
        <Crab />
        <LyricsOverlay />
        <div className="app-shell">
          <main className={'app-main' + (isChat ? ' app-main-chat' : '')}>
            <RoutedBoundary />
          </main>
          {!isChat && <MiniBar />}
          <TabBar />
        </div>
      </MusicProvider>
    </AuthGate>
  );
}
